/**
 * Componente de Login - ILeveTools
 * Esta tela é responsável pela autenticação inicial do usuário com o Microsoft Entra ID.
 * Toda a configuração do SSO está contida e gerenciada diretamente neste arquivo.
 */

import { useState, useEffect } from "react";
import { HeartPulse, Stethoscope, ShieldPlus, Activity } from "lucide-react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { useNavigate } from 'react-router-dom';
import { loginRequest } from '../authConfig';
import { consumeLoginRedirectMessage } from '../utils/sessionAuth';
import { apiFetch } from '../utils/apiFetch';
import { showToast } from '../notifications';

const SERVER_UNAVAILABLE_MESSAGE = 'Servidor indisponível. Contate o time de sistemas.';

// ==========================================
// ESTILOS (CSS in JS)
// ==========================================
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700&display=swap');

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: 'Poppins', sans-serif;
  }

  .page-wrapper {
    min-height: 100vh;
    background-color: #6b1a5f;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    overflow: hidden;
    font-family: 'Poppins', sans-serif;
  }

  .blob {
    position: absolute;
    border-radius: 50%;
    opacity: 0.25;
  }

  .blob-top-right {
    width: 320px;
    height: 320px;
    background: #9b2d8a;
    top: -80px;
    right: -80px;
  }

  .blob-bottom-left {
    width: 260px;
    height: 260px;
    background: #9b2d8a;
    bottom: -60px;
    left: -60px;
  }

  .blob-mid-right {
    width: 180px;
    height: 180px;
    background: #c03aab;
    bottom: 80px;
    right: 20px;
    opacity: 0.15;
  }

  .health-vector {
    position: absolute;
    color: #ffffff;
    opacity: 0.08;
    z-index: 1;
    pointer-events: none;
  }

  .vector-1 { top: 8%; left: 8%; transform: rotate(-15deg); }
  .vector-2 { bottom: 10%; right: 8%; transform: rotate(15deg); }
  .vector-3 { top: 15%; right: 12%; transform: rotate(10deg); }
  .vector-4 { bottom: 15%; left: 10%; transform: rotate(-10deg); }

  .card {
    background: #ffffff;
    border-radius: 24px;
    padding: 45px 50px 50px;
    width: 90%;
    max-width: 500px;
    text-align: center;
    position: relative;
    z-index: 10;
    box-shadow: 0 20px 60px rgba(0,0,0,0.25);
  }

  .logo {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 8px;
  }

  .logo-image {
    height: 80px;
    width: auto;
    object-fit: contain;
  }

  .logo-tagline {
    font-size: 15px; 
    color: #aaa;
    margin-bottom: 30px; 
    font-weight: 400;
    letter-spacing: 0.01em;
  }

  .welcome-title {
    font-size: 30px;
    font-weight: 700;
    color: #6b1a5f;
    margin-bottom: 15px;
  }

  .welcome-desc {
    font-size: 14px;
    color: #888;
    line-height: 1.5;
    margin-bottom: 35px;
    font-weight: 400;
  }

  .ms-button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    width: 100%;
    padding: 14px 24px; 
    background: #8b2fa8;
    color: #ffffff;
    border: none;
    border-radius: 12px;
    font-size: 16px; 
    font-weight: 600;
    font-family: 'Poppins', sans-serif;
    cursor: pointer;
    transition: background 0.2s ease, transform 0.1s ease, box-shadow 0.2s ease;
    letter-spacing: 0.01em;
    box-shadow: 0 4px 16px rgba(139,47,168,0.35);
  }

  .ms-button:hover {
    background: #7a2896;
    box-shadow: 0 6px 22px rgba(139,47,168,0.45);
    transform: translateY(-1px);
  }

  .ms-button:active {
    transform: translateY(0);
    box-shadow: 0 2px 10px rgba(139,47,168,0.3);
  }

  .ms-logo {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr 1fr;
    gap: 2px;
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  .ms-logo span {
    display: block;
    width: 9px;
    height: 9px;
    border-radius: 1px;
  }

  .ms-red    { background: #f25022; }
  .ms-green  { background: #7fba00; }
  .ms-blue   { background: #00a4ef; }
  .ms-yellow { background: #ffb900; }

`;

const PARTIAL_ACCESS_MESSAGE =
  'Você pode usar os conversores. Para cadastros e validações, contacte o administrador.';


// ==========================================
// COMPONENTE REACT
// ==========================================
export default function Login() {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [loading, setLoading] = useState(false);
  const [sessionMessage, setSessionMessage] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return;
    const userEmail = localStorage.getItem('user_email');
    if (isAuthenticated && userEmail) {
      navigate('/Home', { replace: true });
    }
  }, [isAuthenticated, inProgress, navigate]);

  useEffect(() => {
    setSessionMessage(consumeLoginRedirectMessage());
  }, []);

  /**
   * Função que abre o popup da Microsoft usando a instância local, 
   * pega o email e valida no MySQL (Backend Python)
   */
  const handleLogin = async () => {
    setLoading(true);
    try {
      // 1. Abre a tela de login da Microsoft com a configuração interna
      const loginResponse = await instance.loginPopup(loginRequest);

      if (loginResponse.account) {
        instance.setActiveAccount(loginResponse.account);
      }

      const userEmail = loginResponse.account?.username;
      const userName =
        loginResponse.account?.name || userEmail?.split('@')[0] || 'Usuário';

      if (!userEmail) throw new Error("Email não retornado pelo SSO");

      localStorage.setItem('user_email', userEmail);
      localStorage.setItem('user_nome', userName);
      localStorage.removeItem('user_departamento');

      // Perfil em core_usuarios é opcional — SSO OK já libera conversores
      try {
        const apiResponse = await apiFetch('/api/autorizacao', { method: 'POST' });
        if (apiResponse.ok) {
          const data = await apiResponse.json();
          if (data.nome) {
            localStorage.setItem('user_nome', data.nome);
          }
          if (data.cadastrado && data.autorizado && data.departamento) {
            localStorage.setItem('user_departamento', data.departamento);
          } else {
            showToast({ type: 'info', title: 'Acesso parcial', message: PARTIAL_ACCESS_MESSAGE });
          }
        }
      } catch {
        showToast({
          type: 'warning',
          title: 'Perfil não sincronizado',
          message: 'Você pode usar os conversores. A sincronização do perfil será tentada novamente ao usar o sistema.',
        });
      }

      navigate('/Home');

    } catch (error) {
      console.error('Erro na autenticação:', error);
      const message = error instanceof Error ? error.message : String(error);
      if (message.includes('user_cancelled') || message.includes('user_cancelled_error')) {
        return;
      }
      if (message === SERVER_UNAVAILABLE_MESSAGE) {
        showToast({ type: 'error', title: 'Servidor indisponível', message });
      } else {
        showToast({ type: 'error', title: 'Falha na autenticação', message: `Não foi possível autenticar com a Microsoft: ${message}` });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{styles}</style>
      
      <div className="page-wrapper">
        
        {/* --- CAMADA 1: FUNDO --- */}
        <div className="blob blob-top-right" />
        <div className="blob blob-bottom-left" />
        <div className="blob blob-mid-right" />

        <HeartPulse className="health-vector vector-1" size={180} strokeWidth={1} />
        <Stethoscope className="health-vector vector-2" size={220} strokeWidth={1} />
        <ShieldPlus className="health-vector vector-3" size={140} strokeWidth={1} />
        <Activity className="health-vector vector-4" size={160} strokeWidth={1} />

        {/* --- CAMADA 2: CARD PRINCIPAL --- */}
        <div className="card">
          
          <div className="logo">
            <img 
              src="/ilevepdf_logo.png" 
              alt="ILeveTools Logo" 
              className="logo-image" 
            />
          </div>
          <p className="logo-tagline">Ferramentas de conversão e gestão de documentos</p>

          {sessionMessage && (
            <p
              style={{
                background: '#fff4e5',
                color: '#8a4b00',
                border: '1px solid #ffd699',
                borderRadius: '12px',
                padding: '12px 14px',
                marginBottom: '20px',
                fontSize: '14px',
                lineHeight: 1.5,
                textAlign: 'left',
              }}
            >
              {sessionMessage}
            </p>
          )}

          <h2 className="welcome-title">Bem-vindo!</h2>
          <p className="welcome-desc">
            Use sua conta Microsoft corporativa para acessar<br />
            todas as nossas ferramentas de produtividade.
          </p>

          <button 
            className="ms-button" 
            onClick={handleLogin} 
            disabled={loading}
          >
            <div className="ms-logo">
              <span className="ms-red" />
              <span className="ms-green" />
              <span className="ms-blue" />
              <span className="ms-yellow" />
            </div>
            
            {loading ? "Autenticando..." : "Entrar com Microsoft"}
          </button>
          
        </div>
      </div>
    </>
  );
}